import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { salesServicesApi } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { cn } from "../lib/utils";
import {
  ArrowLeft,
  DollarSign,
  Calendar,
  User,
  Clock,
  CheckCircle2,
  AlertCircle,
  History,
  Edit3,
  RefreshCw,
  Building2,
  FileText,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

const PaymentDetailPage = () => {
  const { paymentId } = useParams();
  const navigate = useNavigate();
  
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Status change dialog
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [statusReason, setStatusReason] = useState("");
  const [changingStatus, setChangingStatus] = useState(false);
  
  // Date change dialog
  const [dateDialogOpen, setDateDialogOpen] = useState(false);
  const [newDate, setNewDate] = useState("");
  const [dateReason, setDateReason] = useState("");
  const [changingDate, setChangingDate] = useState(false);

  useEffect(() => {
    fetchPayment();
  }, [paymentId]);

  const fetchPayment = async () => {
    try {
      setLoading(true);
      const res = await salesServicesApi.getOne(paymentId);
      setPayment(res.data);
    } catch (error) {
      toast.error("Failed to load payment");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async () => {
    if (!statusReason.trim()) {
      toast.error("Please provide a reason for the status change");
      return;
    }
    
    setChangingStatus(true);
    try {
      await salesServicesApi.changeStatus(paymentId, {
        new_status: newStatus,
        reason: statusReason
      });
      toast.success("Status changed successfully");
      setStatusDialogOpen(false);
      setStatusReason("");
      fetchPayment();
    } catch (error) {
      toast.error("Failed to change status");
    } finally {
      setChangingStatus(false);
    }
  };

  const handleDateChange = async () => {
    if (!newDate) {
      toast.error("Please select a new date");
      return;
    }
    
    setChangingDate(true);
    try {
      await salesServicesApi.changePaidDate(paymentId, {
        paid_date: newDate,
        reason: dateReason || "Date correction"
      });
      toast.success("Payment date changed");
      setDateDialogOpen(false);
      setDateReason("");
      fetchPayment();
    } catch (error) {
      toast.error("Failed to change date");
    } finally {
      setChangingDate(false);
    }
  };

  const getStatusBadge = () => {
    if (payment?.is_paid) {
      return <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-sm">Paid</Badge>;
    }
    if (payment?.approved_at) {
      return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-sm">Approved</Badge>;
    }
    return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 text-sm">Pending</Badge>;
  };

  const getCurrentStatus = () => {
    if (payment?.is_paid) return "paid";
    if (payment?.approved_at) return "approved";
    return "pending";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="text-center py-8 text-zinc-500">
        <AlertCircle className="h-12 w-12 mx-auto mb-2" />
        <p>Payment not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="text-zinc-400 hover:text-zinc-50 h-8 w-8"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-zinc-50">{payment.name}</h1>
            <p className="text-xs text-zinc-500">
              {payment.project_name} • {payment.month}
            </p>
          </div>
        </div>
        {getStatusBadge()}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Main Info */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-zinc-50 flex items-center gap-2 text-base">
              <DollarSign className="h-4 w-4 text-green-400" />
              Payment Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-zinc-800/50 rounded-lg">
                <p className="text-[10px] text-zinc-500 uppercase">Amount</p>
                <p className="text-lg font-bold text-green-400">
                  {payment.amount?.toLocaleString()} {payment.currency}
                </p>
              </div>
              <div className="p-3 bg-zinc-800/50 rounded-lg">
                <p className="text-[10px] text-zinc-500 uppercase">Month</p>
                <p className="text-lg font-bold text-zinc-50">{payment.month}</p>
              </div>
            </div>

            {payment.project_name && (
              <div className="p-3 bg-zinc-800/50 rounded-lg flex items-center gap-2">
                <Building2 className="h-4 w-4 text-sky-400" />
                <div>
                  <p className="text-[10px] text-zinc-500 uppercase">Project</p>
                  <p className="text-sm text-zinc-50">{payment.project_name}</p>
                </div>
              </div>
            )}

            {payment.client_name && (
              <div className="p-3 bg-zinc-800/50 rounded-lg flex items-center gap-2">
                <User className="h-4 w-4 text-orange-400" />
                <div>
                  <p className="text-[10px] text-zinc-500 uppercase">Client</p>
                  <p className="text-sm text-zinc-50">{payment.client_name}</p>
                </div>
              </div>
            )}

            {payment.service_type && (
              <div className="p-3 bg-zinc-800/50 rounded-lg">
                <p className="text-[10px] text-zinc-500 uppercase">Service Type</p>
                <Badge variant="outline" className="mt-1 bg-zinc-800 text-zinc-300">
                  {payment.service_type}
                </Badge>
              </div>
            )}

            {payment.notes && (
              <div className="p-3 bg-zinc-800/50 rounded-lg">
                <p className="text-[10px] text-zinc-500 uppercase">Notes</p>
                <p className="text-sm text-zinc-300 mt-1">{payment.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Status & Actions */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-zinc-50 flex items-center gap-2 text-base">
              <Clock className="h-4 w-4 text-purple-400" />
              Status & Audit
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Current Status */}
            <div className="p-3 bg-zinc-800/50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] text-zinc-500 uppercase">Current Status</p>
                  <div className="mt-1">{getStatusBadge()}</div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setNewStatus(getCurrentStatus());
                    setStatusDialogOpen(true);
                  }}
                  className="border-zinc-700 text-zinc-400 hover:text-zinc-50 h-8"
                >
                  <RefreshCw className="h-3.5 w-3.5 mr-1" />
                  Change
                </Button>
              </div>
            </div>

            {/* Paid Date */}
            {payment.is_paid && (
              <div className="p-3 bg-green-900/20 border border-green-800/30 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] text-green-500 uppercase">Paid Date</p>
                    <p className="text-sm text-green-400 font-medium mt-1">
                      {payment.paid_date || "Not set"}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setNewDate(payment.paid_date || "");
                      setDateDialogOpen(true);
                    }}
                    className="text-green-400 hover:bg-green-500/10 h-8"
                  >
                    <Edit3 className="h-3.5 w-3.5 mr-1" />
                    Edit
                  </Button>
                </div>
              </div>
            )}

            {/* Audit Info */}
            <div className="space-y-2">
              <p className="text-xs text-zinc-400 font-medium">Audit Trail</p>
              
              <div className="p-2 bg-zinc-800/30 rounded border border-zinc-800 text-xs">
                <div className="flex items-center gap-2">
                  <User className="h-3 w-3 text-zinc-500" />
                  <span className="text-zinc-400">Created by:</span>
                  <span className="text-zinc-300">{payment.created_by_name || "Unknown"}</span>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <Calendar className="h-3 w-3 text-zinc-500" />
                  <span className="text-zinc-400">Created at:</span>
                  <span className="text-zinc-300">{formatDate(payment.created_at)}</span>
                </div>
              </div>

              {payment.approved_at && (
                <div className="p-2 bg-blue-500/5 rounded border border-blue-500/20 text-xs">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-3 w-3 text-blue-400" />
                    <span className="text-blue-400">Approved by:</span>
                    <span className="text-blue-300">{payment.approved_by_name || "Unknown"}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <Calendar className="h-3 w-3 text-blue-400" />
                    <span className="text-blue-400">Approved at:</span>
                    <span className="text-blue-300">{formatDate(payment.approved_at)}</span>
                  </div>
                </div>
              )}

              {payment.paid_at && (
                <div className="p-2 bg-green-500/5 rounded border border-green-500/20 text-xs">
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-3 w-3 text-green-400" />
                    <span className="text-green-400">Paid by:</span>
                    <span className="text-green-300">{payment.paid_by_name || "Unknown"}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <Calendar className="h-3 w-3 text-green-400" />
                    <span className="text-green-400">Marked paid at:</span>
                    <span className="text-green-300">{formatDate(payment.paid_at)}</span>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Status History */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-zinc-50 flex items-center gap-2 text-base">
            <History className="h-4 w-4 text-cyan-400" />
            Change History ({payment.status_history?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!payment.status_history || payment.status_history.length === 0 ? (
            <div className="text-center py-6 text-zinc-500">
              <History className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No changes recorded yet</p>
              <p className="text-xs mt-1">Status and date changes will appear here</p>
            </div>
          ) : (
            <div className="space-y-2">
              {payment.status_history.slice().reverse().map((entry, idx) => (
                <div
                  key={idx}
                  className={cn(
                    "p-3 rounded-lg border text-sm",
                    entry.action === "status_change"
                      ? "bg-purple-500/5 border-purple-500/20"
                      : "bg-cyan-500/5 border-cyan-500/20"
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {entry.action === "status_change" ? (
                          <RefreshCw className="h-3.5 w-3.5 text-purple-400" />
                        ) : (
                          <Calendar className="h-3.5 w-3.5 text-cyan-400" />
                        )}
                        <span className="font-medium text-zinc-300">
                          {entry.action === "status_change" ? "Status Changed" : "Date Changed"}
                        </span>
                        <span className="text-zinc-500">→</span>
                        <Badge variant="outline" className={cn(
                          "text-[10px] h-5",
                          entry.new_value === "paid" ? "bg-green-500/10 text-green-400 border-green-500/30" :
                          entry.new_value === "approved" ? "bg-blue-500/10 text-blue-400 border-blue-500/30" :
                          "bg-yellow-500/10 text-yellow-400 border-yellow-500/30"
                        )}>
                          {entry.action === "date_change" ? entry.new_value : entry.new_value}
                        </Badge>
                        {entry.old_value && (
                          <span className="text-[10px] text-zinc-500">(was: {entry.old_value})</span>
                        )}
                      </div>
                      <p className="text-zinc-400 text-xs">{entry.reason}</p>
                    </div>
                    <div className="text-right text-[10px] text-zinc-500">
                      <p>{entry.changed_by_name}</p>
                      <p>{formatDate(entry.changed_at)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Status Change Dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-purple-400" />
              Change Payment Status
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              This action will be recorded in the audit log.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-zinc-400">New Status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="pending" className="text-yellow-400">
                    Pending
                  </SelectItem>
                  <SelectItem value="approved" className="text-blue-400">
                    Approved
                  </SelectItem>
                  <SelectItem value="paid" className="text-green-400">
                    Paid
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-400">Reason for Change *</Label>
              <Textarea
                value={statusReason}
                onChange={(e) => setStatusReason(e.target.value)}
                placeholder="e.g., Reverting accidental payment mark, Client requested delay, etc."
                className="bg-zinc-800 border-zinc-700 text-zinc-50 min-h-[80px]"
              />
              <p className="text-[10px] text-zinc-500">
                This reason will be visible in the change history
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setStatusDialogOpen(false)}
                className="border-zinc-700"
              >
                Cancel
              </Button>
              <Button
                onClick={handleStatusChange}
                disabled={changingStatus || !statusReason.trim()}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {changingStatus ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Change Status
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Date Change Dialog */}
      <Dialog open={dateDialogOpen} onOpenChange={setDateDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <Calendar className="h-5 w-5 text-cyan-400" />
              Change Payment Date
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              This correction will be recorded in the audit log.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-zinc-400">New Date</Label>
              <Input
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-zinc-50"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-400">Reason (optional)</Label>
              <Textarea
                value={dateReason}
                onChange={(e) => setDateReason(e.target.value)}
                placeholder="e.g., Actual bank transfer date, Accounting correction, etc."
                className="bg-zinc-800 border-zinc-700 text-zinc-50 min-h-[60px]"
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setDateDialogOpen(false)}
                className="border-zinc-700"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDateChange}
                disabled={changingDate || !newDate}
                className="bg-cyan-600 hover:bg-cyan-700"
              >
                {changingDate ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Calendar className="h-4 w-4 mr-2" />
                )}
                Update Date
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentDetailPage;
